import { createSlice } from "@reduxjs/toolkit";
import { boardViewForAllocation } from "./TransferOrderSliceV2";
import { sortAllView } from "../utils";

const syncHistoryDefaultColumns = [
  "APPLICATION",
  "STATUS",
  "DATE",
  "TIME",
  "SYNC ACTION",
  "COMMENT",
];

const shopifyStoresDefaultColumns = [
  "STORE NAME",
  "STATUS",
  "ACTIVE",
  "PRODUCT CATALOGUE",
  "MANAGE",
];

const shopifyCatalogueDefaultColumns = [
  "PRODUCT",
  "PRICELIST",
  "FACILITIES",
  "AVAILABLE STOCK",
  "ALLOW BACKORDER",
  "STATUS",
];

const shopifyLinkedProductsDefaultColumns = [
  "PRODUCT",
  "SHOPIFY PRODUCT",
  "SHOPIFY AVAILABLE",
  "SHOPIFY ON HAND",
  "STOCK BY LOCATION",
  "ON ORDER",
  "NEXT ETA",
  "OPEN PURCHASE ORDERS",
];

const initialState = {
  operations: { count: 0, results: [] },
  operationsParams: { limit: 10, count: 0 },

  syncHistory: { count: 0, results: [] },
  syncHistoryParams: { limit: 10, offset: 0 },
  syncHistoryLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: syncHistoryDefaultColumns,
      allViews: {
        "Default View": syncHistoryDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  shopifyStores: { count: 0, results: [] },
  shopifyStoresParams: { limit: 10, offset: 0 },
  shopifyStoresLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: shopifyStoresDefaultColumns,
      allViews: {
        "Default View": shopifyStoresDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  shopifyCatalogue: { count: 0, results: [] },
  shopifyCatalogueParams: { limit: 10, offset: 0 },
  shopifyCatalogueLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: shopifyCatalogueDefaultColumns,
      allViews: {
        "Default View": shopifyCatalogueDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  shopifyLinkedProducts: { count: 0, results: [] },
  shopifyLinkedProductsParams: { limit: 10, offset: 0 },
  shopifyLinkedProductsLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: shopifyLinkedProductsDefaultColumns,
      allViews: {
        "Default View": shopifyLinkedProductsDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  externalSystems: [],
  inventoryPlanner: {},
  xero: {},
  linkedTaxRates: [],
  linkedAccounts: [],

  syncSchedule: null,
};

const integrationSlice = createSlice({
  name: "integration",
  initialState,
  reducers: {
    createInventoryPlanner: (state, action) => {
      state.inventoryPlanner = action.payload;
    },
    resetOperations: state => {
      state.operations = initialState.operations;
    },
    resetOperationsParams: state => {
      state.operationsParams = { limit: state.operationsParams.limit, offset: 0 };
    },
    setOperations: (state, action) => {
      state.operations = { count: action.payload.count, results: action.payload.results };
    },
    setLinkedTaxRates: (state, action) => {
      state.linkedTaxRates = action.payload;
    },
    setLinkedAccounts: (state, action) => {
      state.linkedAccounts = action.payload;
    },
    setOperationsParams: (state, action) => {
      state.operationsParams = action.payload;
    },
    setSyncSchedule: (state, action) => {
      state.syncSchedule = action.payload;
    },
    setSyncHistoryLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.syncHistoryLayout = {
        ...state.syncHistoryLayout,
        listView: {
          ...state.syncHistoryLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: {
            ...state.syncHistoryLayout.listView.allViews,
            ...views.list.allViews,
          },
        },
        completeListOfViews: action.payload,
      };
    },
    setSyncHistory: (state, action) => {
      state.syncHistory = action.payload;
    },
    setSyncHistoryParams: (state, action) => {
      state.syncHistoryParams = action.payload;
    },
    setShopifyStores: (state, action) => {
      state.shopifyStores = action.payload;
    },
    setShopifyStoresParams: (state, action) => {
      state.shopifyStoresParams = action.payload;
    },
    updateShopifyStoresParams: (state, action) => {
      state.shopifyStoresParams = { ...state.shopifyStoresParams, ...action.payload };
    },
    updateShopifyStoreInList: (state, action) => {
      const store = action.payload;
      state.shopifyStores.results = state.shopifyStores.results.map(item =>
        item.id === store.id ? { ...item, ...store } : item,
      );
    },
    setShopifyStoresLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.shopifyStoresLayout = {
        ...state.shopifyStoresLayout,
        listView: {
          ...state.shopifyStoresLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: {
            ...state.shopifyStoresLayout.listView.allViews,
            ...views.list.allViews,
          },
        },
        completeListOfViews: action.payload,
      };
    },
    setShopifyCatalogue: (state, action) => {
      state.shopifyCatalogue = action.payload;
    },
    setShopifyCatalogueParams: (state, action) => {
      state.shopifyCatalogueParams = action.payload;
    },
    updateShopifyCatalogueParams: (state, action) => {
      state.shopifyCatalogueParams = {
        ...state.shopifyCatalogueParams,
        ...action.payload,
      };
    },
    setShopifyCatalogueLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.shopifyCatalogueLayout = {
        ...state.shopifyCatalogueLayout,
        listView: {
          ...state.shopifyCatalogueLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: {
            ...state.shopifyCatalogueLayout.listView.allViews,
            ...views.list.allViews,
          },
        },
        completeListOfViews: action.payload,
      };
    },
    setShopifyLinkedProducts: (state, action) => {
      state.shopifyLinkedProducts = action.payload;
    },
    setShopifyLinkedProductsParams: (state, action) => {
      state.shopifyLinkedProductsParams = action.payload;
    },
    updateShopifyLinkedProductsParams: (state, action) => {
      state.shopifyLinkedProductsParams = {
        ...state.shopifyLinkedProductsParams,
        ...action.payload,
      };
    },
    setShopifyLinkedProductsLayoutAllViews: (state, action) => {
      const views = sortAllView(action.payload);
      state.shopifyLinkedProductsLayout = {
        ...state.shopifyLinkedProductsLayout,
        listView: {
          ...state.shopifyLinkedProductsLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: {
            ...state.shopifyLinkedProductsLayout.listView.allViews,
            ...views.list.allViews,
          },
        },
        completeListOfViews: action.payload,
      };
    },
    updateExternalSystemInOperations: (state, action) => {
      state.operations.results.forEach(item => {
        item.externalsystemoperation_set.forEach(obj => {
          if (obj.external_system.id === action.payload.id) {
            obj.external_system.is_config_exists = true;
          }
        });
      });
    },
    updateExternalSystemOperationOptedIn: (state, action) => {
      state.operations.results.forEach(item => {
        item.externalsystemoperation_set.forEach(obj => {
          if (obj.id === action.payload) {
            obj.opted_in = true;
          }
        });
      });
    },
    updateInventoryPlanner: (state, action) => {
      state.inventoryPlanner = action.payload;
    },
    updateIntegrationStatusByCode: (state, action) => {
      const {
        result: { is_active },
        code,
      } = action.payload;

      state.externalSystems = state.externalSystems.map(item => {
        if (item?.code === code) {
          item.connect_config[0].is_active = is_active;
        }

        return item;
      });
    },
    updateOperationsParams: (state, action) => {
      state.operationsParams = { ...state.operationsParams, ...action.payload };
    },
    updateSyncHistoryParams: (state, action) => {
      state.syncHistoryParams = { ...state.syncHistoryParams, ...action.payload };
    },
    getExternalSystems: (state, action) => {
      state.externalSystems = action.payload;
    },
    getInventoryPlanner: (state, action) => {
      state.inventoryPlanner = action.payload;
    },
    getXero: (state, action) => {
      state.xero = action.payload;
    },
    resetXero: state => {
      state.xero = {};
      state.externalSystems = state.externalSystems.map(item => {
        if (item?.code === "xero") {
          item.connect_config = [];
        }
        return item;
      });
    },
    resetExternalSystems: state => {
      state.externalSystems = initialState.externalSystems;
    },
  },
});

export const {
  createInventoryPlanner,
  resetOperations,
  resetOperationsParams,
  setOperations,
  setOperationsParams,
  setSyncSchedule,
  setSyncHistory,
  setSyncHistoryParams,
  setSyncHistoryLayoutAllViews,
  setShopifyStores,
  setShopifyStoresParams,
  updateShopifyStoresParams,
  updateShopifyStoreInList,
  setShopifyStoresLayoutAllViews,
  setShopifyCatalogue,
  setShopifyCatalogueParams,
  updateShopifyCatalogueParams,
  setShopifyCatalogueLayoutAllViews,
  setShopifyLinkedProducts,
  setShopifyLinkedProductsParams,
  updateShopifyLinkedProductsParams,
  setShopifyLinkedProductsLayoutAllViews,
  setLinkedTaxRates,
  updateExternalSystemInOperations,
  updateExternalSystemOperationOptedIn,
  updateInventoryPlanner,
  updateIntegrationStatusByCode,
  updateOperationsParams,
  updateSyncHistoryParams,
  getExternalSystems,
  getInventoryPlanner,
  setLinkedAccounts,
  getXero,
  resetXero,
  resetExternalSystems,
} = integrationSlice.actions;

export default integrationSlice.reducer;
